import { useEffect, useState, useMemo, useCallback, useRef, lazy, Suspense } from "react";
import {
  Form,
  Dropdown,
  Nav,
  Offcanvas,
  Button,
  InputGroup,
  Badge,
} from "react-bootstrap";
import { BsFilter, BsPeople, BsEnvelopeArrowDown, BsSearch, BsSortDown } from "react-icons/bs";
import { useFindUsers } from "../../api/queries/useFindUser";
import { User, UserImageUrls } from "../../types/Types";
import { useGetConnectionRequests } from "../../api/queries/useGetConnectionRequests";
import { useGetSentConnectionRequests } from "../../api/queries/useGetSentConnectionsRequests";
import { useGetConnections } from "../../api/queries/useGetConnections";
import { useAppContext } from "../../Context/AppContext";
import { countries } from "countries-list";
import { getCountryFlagUrl } from "../../utils/countryFlag";

const UsersList = lazy(() => import("./UsersList"));
const ConnectionsList = lazy(() => import("./ConnectionList"));
const RequestsList = lazy(() => import("./RequestsList"));

const SECTORS = [
  "Accounting and Finance",
  "Technology & IT",
  "Retail & E-Commerce",
  "Healthcare & Medical",
  "Education & Training",
  "Other",
] as const;

const TURNOVER_OPTIONS = [
  "0-100K",
  "100K-500K",
  "500K-1M",
  "1M-5M",
  "5M-10M",
  "10M+",
] as const;

const COUNTRY_OPTIONS = Object.freeze(
  Object.values(countries)
    .map((c) => c.name)
    .sort()
);

/** Pure helper: upgrade message for filter based on membership and filter type. */
function getUpgradeMessage(
  membershipType: string,
  filterType: string
): string {
  if (membershipType === "youth") {
    return "Upgrade to Business or Executive membership to use this filter";
  }
  if (membershipType === "business") {
    if (filterType === "turnover" || filterType === "country") {
      return "Upgrade to Executive membership to use this filter";
    }
  }
  return "";
}

// Styles moved to CSS classes

const BODY_CLASS_MOBILE_SCROLL_LOCK = "connections-mobile-noscroll";

function Connections() {
  const { objectId, user: currentUser } = useAppContext();
  const userId = objectId ?? "";

  /* Lock body scroll on mobile so only the inner scroll area scrolls; desktop unchanged */
  useEffect(() => {
    const applyLock = () => {
      if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) {
        document.body.classList.add(BODY_CLASS_MOBILE_SCROLL_LOCK);
      }
    };
    const removeLock = () => document.body.classList.remove(BODY_CLASS_MOBILE_SCROLL_LOCK);
    const handleChange = (e: MediaQueryListEvent) => {
      if (e.matches) applyLock();
      else removeLock();
    };
    applyLock();
    const mql = window.matchMedia("(max-width: 767px)");
    mql.addEventListener("change", handleChange);
    return () => {
      removeLock();
      mql.removeEventListener("change", handleChange);
    };
  }, []);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("Recently added");
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [fullUsersList, setFullUsersList] = useState<User[]>([]);
  const [fullUsersImages, setFullUsersImages] = useState<UserImageUrls[]>([]);

  // Filter states
  const [appliedFilters, setAppliedFilters] = useState({
    cities: [] as string[],
    sectors: [] as string[],
    turnovers: [] as string[],
    countries: [] as string[],
  });

  // Filter input states
  const [cityFilter, setCityFilter] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [turnoverFilter, setTurnoverFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");

  const [showFilters, setShowFilters] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<
    "connections" | "requests" | "users"
  >("users");
  /* ================= MEMBERSHIP HELPERS ================= */

  const membershipType = useMemo(() => {
    const membership = currentUser?.user?.membershipName?.toLowerCase() || "";
    if (membership === "youth membership") return "youth";
    if (membership === "business membership") return "business";
    if (membership === "executive membership") return "executive";
    return "youth";
  }, [currentUser?.user?.membershipName]);
  const canUseCityFilter = membershipType === "business" || membershipType === "executive";
  const canUseSectorFilter = membershipType === "business" || membershipType === "executive";
  const canUseTurnoverFilter = membershipType === "executive";
  const canUseCountryFilter = membershipType === "executive";

  /* ================= API ================= */

  const { data: userResponse, refetch: refetchUsers } = useFindUsers({
    pageSize: 50,
    page: currentPage,
    searchTerm: debouncedSearchTerm || undefined,
    cities: canUseCityFilter && appliedFilters.cities.length > 0 ? appliedFilters.cities : undefined,
    sectors: canUseSectorFilter && appliedFilters.sectors.length > 0 ? appliedFilters.sectors : undefined,
    turnovers: canUseTurnoverFilter && appliedFilters.turnovers.length > 0 ? appliedFilters.turnovers : undefined,
    countries: canUseCountryFilter && appliedFilters.countries.length > 0 ? appliedFilters.countries : undefined,
  });

  const { data: connectionRequests, refetch: refetchConnectionRequests } =
    useGetConnectionRequests({ userId });

  const { data: sentConnectionRequests, refetch: refetchSentConnectionRequests } =
    useGetSentConnectionRequests({ userId });

  const { data: connections, refetch: refetchConnections } =
    useGetConnections({ userId });

  /* ================= EFFECTS ================= */

  // Reset pagination and clear users list when search or filters change
  useEffect(() => {
    setCurrentPage(1);
    setHasMoreUsers(true);
    // Clear the users list when filters change
    setFullUsersList([]);
    setFullUsersImages([]);
  }, [debouncedSearchTerm, appliedFilters]);

  useEffect(() => {
    if (userResponse) {
      const newUsers = userResponse?.users?.data ?? [];
      const newImages = userResponse?.userImageUrls ?? [];

      // If it's the first page, replace the list; otherwise append (avoid duplicates)
      if (currentPage === 1) {
        setFullUsersList(newUsers);
        setFullUsersImages(newImages);
      } else {
        // Append only new users (avoid duplicates by checking id)
        setFullUsersList((p) => {
          const existingIds = new Set(p.map(u => u.id));
          const uniqueNewUsers = newUsers.filter(u => !existingIds.has(u.id));
          return [...p, ...uniqueNewUsers];
        });
        setFullUsersImages((p) => {
          const existingIds = new Set(p.map(img => img.userId));
          const uniqueNewImages = newImages.filter(img => !existingIds.has(img.userId));
          return [...p, ...uniqueNewImages];
        });
      }

      // Update hasMoreUsers based on totalPages
      const totalPages = userResponse?.users?.totalPages ?? 0;
      const currentPageNum = userResponse?.users?.pageNumber ?? currentPage;
      setHasMoreUsers(currentPageNum < totalPages);
    }
  }, [userResponse, currentPage]);

  // Refetch list when switching tabs
  useEffect(() => {
    if (activeTab === "connections") refetchConnections();
    if (activeTab === "requests") refetchConnectionRequests();
    if (activeTab === "users") refetchUsers();
  }, [activeTab, refetchConnections, refetchConnectionRequests, refetchUsers]);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
  
    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  /* ================= FILTER FUNCTIONS ================= */

  const addFilter = useCallback(
    (key: keyof typeof appliedFilters, value: string) => {
      if (!value.trim()) return;
      setAppliedFilters((prev) => {
        const newFilters = { ...prev };
        if (!newFilters[key].includes(value)) {
          newFilters[key] = [...newFilters[key], value];
        }
        return newFilters;
      });
    },
    []
  );

  const removeFilter = useCallback(
    (key: keyof typeof appliedFilters, index: number) => {
      setAppliedFilters((prev) => ({
        ...prev,
        [key]: prev[key].filter((_, i) => i !== index),
      }));
    },
    []
  );

  const clearAllFilters = useCallback(() => {
    setAppliedFilters({
      cities: [],
      sectors: [],
      turnovers: [],
      countries: [],
    });
  }, []);

  const hasActiveFilters = useMemo(
    () =>
      appliedFilters.cities.length > 0 ||
      appliedFilters.sectors.length > 0 ||
      appliedFilters.turnovers.length > 0 ||
      appliedFilters.countries.length > 0,
    [
      appliedFilters.cities.length,
      appliedFilters.sectors.length,
      appliedFilters.turnovers.length,
      appliedFilters.countries.length,
    ]
  );

  // Clear restricted filters when membership changes
  useEffect(() => {
    if (membershipType === "youth") {
      clearAllFilters();
    } else if (membershipType === "business") {
      setAppliedFilters((prev) => ({
        ...prev,
        turnovers: [],
        countries: [],
      }));
    }
  }, [membershipType, clearAllFilters]);

  /* ================= HELPER FUNCTIONS ================= */

  const mergeImages = useCallback((items: User[]) => {
    if (!items) return [];
    return items.map((u) => ({
      ...u,
      ...fullUsersImages.find((i) => i.userId === u.id)?.imageUrls,
    }));
  }, [fullUsersImages]);

  const sortUsers = useCallback((items: User[] = []) => {
    if (!items) return [];
    return [...items].sort((a, b) => {
      if (sortOption === "First Name") {
        const aName = (a.firstName || "").trim();
        const bName = (b.firstName || "").trim();
        return aName.localeCompare(bName);
      } else if (sortOption === "Last Name") {
        const aName = (a.lastName || "").trim();
        const bName = (b.lastName || "").trim();
        return aName.localeCompare(bName);
      }
      return 0;
    });
  }, [sortOption]);

  const filterExcludedUsers = useCallback((items: User[] = []) => {
    if (!items) return [];
    return items.filter(
      (u, i, self) =>
        i === self.findIndex((x) => x.objectId === u.objectId) &&
        u.objectId !== userId &&
        !connections?.userDtos?.some((c) => c.objectId === u.objectId) &&
        !connectionRequests?.userDtos?.some((r) => r.objectId === u.objectId)
    );
  }, [userId, connections?.userDtos, connectionRequests?.userDtos]);

  const filterUsersBySearch = useCallback((items: User[] = [], term: string) => {
    if (!items.length || !term.trim()) return items;
    const q = term.trim().toLowerCase();
    return items.filter((u) => {
      const first = (u.firstName || "").toLowerCase();
      const last = (u.lastName || "").toLowerCase();
      const full = `${first} ${last}`.trim();
      return first.includes(q) || last.includes(q) || full.includes(q);
    });
  }, []);

  const users = useMemo(
    () =>
      filterExcludedUsers(sortUsers(mergeImages(fullUsersList))),
    [
      fullUsersList,
      mergeImages,
      sortUsers,
      filterExcludedUsers,
    ]
  );

  const connectionsListData = useMemo(
    () => mergeImages(sortUsers(connections?.userDtos ?? [])),
    [connections?.userDtos, mergeImages, sortUsers]
  );

  const requestsListData = useMemo(
    () => mergeImages(sortUsers(connectionRequests?.userDtos ?? [])),
    [connectionRequests?.userDtos, mergeImages, sortUsers]
  );

  const sentRequestsListData = useMemo(
    () => mergeImages(sortUsers(sentConnectionRequests?.userDtos ?? [])),
    [sentConnectionRequests?.userDtos, mergeImages, sortUsers]
  );

  const filteredConnectionsListData = useMemo(
    () => filterUsersBySearch(connectionsListData, searchTerm),
    [connectionsListData, searchTerm, filterUsersBySearch]
  );

  const filteredRequestsListData = useMemo(
    () => filterUsersBySearch(requestsListData, searchTerm),
    [requestsListData, searchTerm, filterUsersBySearch]
  );

  const filteredSentRequestsListData = useMemo(
    () => filterUsersBySearch(sentRequestsListData, searchTerm),
    [sentRequestsListData, searchTerm, filterUsersBySearch]
  );

  const loadMoreUsers = useCallback(
    () => setCurrentPage((p) => p + 1),
    []
  );

  const handleOpenFilters = useCallback(() => setShowFilters(true), []);
  const handleCloseFilters = useCallback(() => setShowFilters(false), []);
  const handleTabSelect = useCallback(
    (k: string | null) => k && setActiveTab(k as "connections" | "requests" | "users"),
    []
  );
  const handleSortSelect = useCallback((k: string | null) => {
    if (k) setSortOption(k);
  }, []);

  /* ================= UI ================= */

  return (
    <div className="connections-root p-3 p-md-4 position-relative pt-5 pt-md-5 d-flex flex-column min-vh-100 vh-100 overflow-hidden min-vw-0">
      {/* Header: sticky on mobile (via CSS), fixed at top on desktop (scroll area below) */}
      <div className="connections-header-sticky bg-white pb-2 flex-shrink-0">
      {/* Desktop: Header Row - fixed min-height to prevent layout shift when switching tabs */}
      <div className="d-none d-md-flex flex-row justify-content-between align-items-center mb-3 connections-desktop-header-row">
        <h5 className="fw-bold mb-0 text-dark">Connections</h5>
        {activeTab === "users" && (
          <Button
            variant="outline-primary"
            className="d-flex align-items-center gap-2 position-relative rounded-2 shadow-none px-2"
            style={{ paddingTop: 6, paddingBottom: 6 }}
            onClick={handleOpenFilters}
            aria-label="Filter"
          >
            <BsFilter size={16} aria-hidden />
            <span>Filter</span>
            {hasActiveFilters && (
              <Badge bg="primary" pill className="position-absolute top-0 start-100 translate-middle">
                {appliedFilters.cities.length + appliedFilters.sectors.length + appliedFilters.turnovers.length + appliedFilters.countries.length}
              </Badge>
            )}
          </Button>
        )}
      </div>

      {/* Mobile: Second-image layout - Subtitle first, then Connections title */}
      <div className="d-md-none mb-3">
        {activeTab === "requests" && (
          <div className="d-flex flex-row justify-content-between align-items-baseline w-100 mb-1 gap-2 min-w-0">
            <span className="text-muted small text-nowrap connections-mobile-subtitle">Incoming requests</span>
            <span className="text-muted text-end small text-nowrap connections-mobile-subtitle">Accept to add to your network</span>
          </div>
        )}
        {activeTab === "connections" && (
          <div className="d-flex flex-row justify-content-between align-items-baseline w-100 mb-1">
            <span className="text-muted small connections-mobile-subtitle">Your connections</span>
            <span className="text-muted text-end small connections-mobile-subtitle">{filteredConnectionsListData.length} connected</span>
          </div>
        )}
        <h5 className="fw-bold mb-0 text-dark">Connections</h5>
      </div>

      {/* FILTER SIDEBAR / MODAL: offcanvas on desktop, full-screen modal on mobile */}
      <Offcanvas
        show={showFilters}
        onHide={handleCloseFilters}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Filters</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          {/* City Filter */}
          <div className="mb-3">
            <label className="form-label small fw-bold">City</label>
            <InputGroup size="sm">
              <Form.Control
                placeholder="Enter city"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                disabled={!canUseCityFilter}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && canUseCityFilter) {
                    e.preventDefault();
                    addFilter("cities", cityFilter);
                    setCityFilter("");
                  }
                }}
              />
              <Button
                variant="outline-secondary"
                onClick={() => {
                  if (canUseCityFilter) {
                    addFilter("cities", cityFilter);
                    setCityFilter("");
                  }
                }}
                disabled={!canUseCityFilter}
              >
                Add
              </Button>
            </InputGroup>
            {!canUseCityFilter && (
              <div className="text-muted small mt-1">
                {getUpgradeMessage(membershipType, "city")}
              </div>
            )}
            {appliedFilters.cities.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {appliedFilters.cities.map((city, i) => (
                  <span key={i} className="badge bg-primary">
                    {city}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      onClick={() => removeFilter("cities", i)}
                      aria-label="Remove"
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Sector Filter */}
          <div className="mb-3">
            <label className="form-label small fw-bold">Sector / Industry</label>
            <Form.Select
              size="sm"
              value={sectorFilter}
              onChange={(e) => {
                if (e.target.value && canUseSectorFilter) {
                  addFilter("sectors", e.target.value);
                  setSectorFilter("");
                }
              }}
              disabled={!canUseSectorFilter}
            >
              <option value="">Select sector</option>
              {SECTORS.map((s, i) => (
                <option key={i} value={s}>
                  {s}
                </option>
              ))}
            </Form.Select>
            {!canUseSectorFilter && (
              <div className="text-muted small mt-1">
                {getUpgradeMessage(membershipType, "sector")}
              </div>
            )}
            {appliedFilters.sectors.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {appliedFilters.sectors.map((sector, i) => (
                  <span key={i} className="badge bg-primary">
                    {sector}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      onClick={() => removeFilter("sectors", i)}
                      aria-label="Remove"
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Turnover Filter */}
          <div className="mb-3">
            <label className="form-label small fw-bold">Business Turnover</label>
            <Form.Select
              size="sm"
              value={turnoverFilter}
              onChange={(e) => {
                if (e.target.value && canUseTurnoverFilter) {
                  addFilter("turnovers", e.target.value);
                  setTurnoverFilter("");
                }
              }}
              disabled={!canUseTurnoverFilter}
            >
              <option value="">Select turnover</option>
              {TURNOVER_OPTIONS.map((t, i) => (
                <option key={i} value={t}>
                  {t}
                </option>
              ))}
            </Form.Select>
            {!canUseTurnoverFilter && (
              <div className="text-muted small mt-1">
                {getUpgradeMessage(membershipType, "turnover")}
              </div>
            )}
            {appliedFilters.turnovers.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {appliedFilters.turnovers.map((turnover, i) => (
                  <span key={i} className="badge bg-primary">
                    {turnover}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      onClick={() => removeFilter("turnovers", i)}
                      aria-label="Remove"
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Country Filter */}
          <div className="mb-3">
            <label className="form-label small fw-bold">Country</label>
            <Form.Select
              size="sm"
              value={countryFilter}
              onChange={(e) => {
                if (e.target.value && canUseCountryFilter) {
                  addFilter("countries", e.target.value);
                  setCountryFilter("");
                }
              }}
              disabled={!canUseCountryFilter}
            >
              <option value="">Select country</option>
              {COUNTRY_OPTIONS.map((c, i) => (
                <option key={i} value={c}>
                  {c}
                </option>
              ))}
            </Form.Select>
            {!canUseCountryFilter && (
              <div className="text-muted small mt-1">
                {getUpgradeMessage(membershipType, "country")}
              </div>
            )}
            {appliedFilters.countries.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {appliedFilters.countries.map((country, i) => (
                  <span key={i} className="badge bg-primary d-inline-flex align-items-center gap-1">
                    {getCountryFlagUrl(country) && (
                      <img
                        src={getCountryFlagUrl(country)!}
                        alt=""
                        width={24}
                        height={18}
                        className="rounded object-fit-cover"
                      />
                    )}
                    {country}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      onClick={() => removeFilter("countries", i)}
                      aria-label="Remove"
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Clear All Filters */}
          {hasActiveFilters && (
            <Button
              variant="outline-danger"
              size="sm"
              className="w-100 mb-3"
              onClick={clearAllFilters}
            >
              Clear All Filters
            </Button>
          )}

          <Button
            variant="primary"
            className="w-100"
            onClick={handleCloseFilters}
          >
            Apply Filters
          </Button>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Tablet (768–991px): stacked – tabs full width, then search+sort. Desktop (992px+): tabs left, search+sort right */}
      <div className="d-none d-md-flex flex-column flex-lg-row flex-nowrap align-items-end mb-1 gap-2">
        {/* Tabs: full width on tablet only */}
        <div className="connections-tabs-tablet-full bg-light rounded-2 border shadow-sm d-flex p-1 flex-shrink-0">
          <Nav
            activeKey={activeTab}
            onSelect={handleTabSelect}
            className="d-flex flex-nowrap gap-2"
          >
            <Nav.Item>
              <Nav.Link
                eventKey="users"
                className={`d-flex align-items-center gap-2 rounded-2 text-dark text-decoration-none border-0 px-2 py-1 ${activeTab === "users" ? "shadow bg-white" : "bg-transparent"}`}
                title="Search and discover all users"
              >
                <BsSearch size={16} className={`flex-shrink-0 ${activeTab === "users" ? "text-primary" : "text-dark"}`} aria-hidden />
                <span className="d-flex flex-column align-items-start text-start small lh-sm">
                  <span className={activeTab === "users" ? "text-primary fw-bold" : "text-dark fw-normal"}>Discover</span>
                  <span className="text-muted small">Discover people</span>
                </span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="requests"
                className={`d-flex align-items-center gap-2 rounded-2 text-dark text-decoration-none border-0 px-2 py-1 ${activeTab === "requests" ? "shadow bg-white" : "bg-transparent"}`}
                title="Pending connection requests"
              >
                <BsEnvelopeArrowDown size={16} className={`flex-shrink-0 ${activeTab === "requests" ? "text-primary" : "text-dark"}`} aria-hidden />
                <span className="d-flex flex-column align-items-start text-start small lh-sm">
                  <span className={`d-inline-flex align-items-center gap-1 ${activeTab === "requests" ? "text-primary fw-bold" : "text-dark fw-normal"}`}>
                    Requests
                    {requestsListData.length > 0 && (
                      <Badge bg="primary" pill className="small px-2">{requestsListData.length}</Badge>
                    )}
                  </span>
                  <span className="text-muted small">Pending invites</span>
                </span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="connections"
                className={`d-flex align-items-center gap-2 rounded-2 text-dark text-decoration-none border-0 px-2 py-1 ${activeTab === "connections" ? "shadow bg-white" : "bg-transparent"}`}
                title="People you are connected with"
              >
                <BsPeople size={16} className={`flex-shrink-0 ${activeTab === "connections" ? "text-primary" : "text-dark"}`} aria-hidden />
                <span className="d-flex flex-column align-items-start text-start small lh-sm">
                  <span className={activeTab === "connections" ? "text-primary fw-bold" : "text-dark fw-normal"}>My Network</span>
                  <span className="text-muted small">Your network</span>
                </span>
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        {/* Search + Sort: full width on tablet, grows on desktop */}
        <div className="d-flex align-items-center gap-1 flex-grow-1 justify-content-end w-100 min-w-0" style={{ minWidth: '300px', maxWidth: '100%' }}>
          <InputGroup className="rounded-2 flex-grow-1 connections-search-desktop">
            <InputGroup.Text className="bg-white border border-end-0 rounded-start d-flex align-items-center px-1 px-md-2">
              <BsSearch size={16} className="text-secondary" aria-hidden />
            </InputGroup.Text>
            <Form.Control
              ref={searchInputRef}
              placeholder="Search by name, sector, or location"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search"
              className="border border-start-0 rounded-end bg-white shadow-none"
              style={{ paddingTop: 6, paddingBottom: 6 }}
            />
          </InputGroup>
          <Dropdown onSelect={handleSortSelect}>
            <Dropdown.Toggle
              variant="outline-primary"
              className="d-flex align-items-center gap-2 rounded-2 shadow-none flex-shrink-0 px-2"
              style={{ paddingTop: 6, paddingBottom: 6 }}
              aria-label="Sort by"
            >
              <BsSortDown size={16} aria-hidden />
              <span>Sort</span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item eventKey="Recently added">Recently added</Dropdown.Item>
              <Dropdown.Item eventKey="First Name">First Name</Dropdown.Item>
              <Dropdown.Item eventKey="Last Name">Last Name</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* Mobile: Navigation Tabs – compact size, rounded container, icon above text */}
      <nav className="d-sm-none mb-2 position-relative" aria-label="Connections">
        <div className="bg-light rounded-2 border shadow-sm d-flex p-1 overflow-x-auto">
          <button
            type="button"
            className={`d-flex flex-column align-items-center justify-content-center gap-1 border-0 flex-fill py-1 px-0 rounded-1 ${activeTab === "users" ? "bg-primary text-white fw-semibold" : "bg-white text-dark"}`}
            onClick={() => setActiveTab("users")}
            aria-label="Discover – Search and discover all users"
          >
            <BsSearch size={14} aria-hidden />
            <span className="small">Discover</span>
          </button>
          <button
            type="button"
            className={`d-flex flex-column align-items-center justify-content-center gap-1 border-0 flex-fill position-relative py-1 px-0 rounded-1 ${activeTab === "requests" ? "bg-primary text-white fw-semibold" : "bg-white text-dark"}`}
            onClick={() => setActiveTab("requests")}
            aria-label="Requests – Pending connection requests"
          >
            <BsEnvelopeArrowDown size={14} aria-hidden />
            <span className="small">Requests</span>
          </button>
          <button
            type="button"
            className={`d-flex flex-column align-items-center justify-content-center gap-1 border-0 flex-fill py-1 px-0 rounded-1 ${activeTab === "connections" ? "bg-primary text-white fw-semibold" : "bg-white text-dark"}`}
            onClick={() => setActiveTab("connections")}
            aria-label="My Network – People you are connected with"
          >
            <BsPeople size={14} aria-hidden />
            <span className="small">My Network</span>
          </button>
        </div>
      </nav>

      {/* Mobile: Search bar – full placeholder visible like reference image */}
      <div className="d-flex d-md-none flex-column gap-2 mb-0">
        <InputGroup className="rounded-3 border shadow-sm w-100 bg-white min-w-0">
          <Form.Control
            ref={searchInputRef}
            placeholder="Search by name, sector, or location"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search by name, sector, or location"
            className="border-0 border-end rounded-start bg-white shadow-none py-2 px-3 flex-grow-1 min-w-0 connections-search-placeholder"
          />
          <InputGroup.Text className="bg-white border-0 border-start rounded-end d-flex align-items-center px-2 py-2 flex-shrink-0">
            <BsSearch size={16} className="text-secondary" aria-hidden />
          </InputGroup.Text>
        </InputGroup>
      </div>
      </div>
      {/* END FIXED – scrollable section below */}

      <div className="connections-scroll-area d-flex flex-column pt-2 pt-md-0 overflow-y-auto flex-grow-1 min-h-0">
        {activeTab === "users" && (
          <div className="d-flex d-md-none align-items-stretch justify-content-between gap-2 w-100 mb-2">
            <Button
              variant="outline-primary"
              className="d-flex align-items-center gap-2 position-relative rounded-2 text-nowrap shadow-none py-2 px-3"
              onClick={handleOpenFilters}
              aria-label="Filter"
            >
              <BsFilter size={16} aria-hidden />
              <span>Filters</span>
              {hasActiveFilters && (
                <Badge bg="primary" pill className="position-absolute top-0 start-100 translate-middle">
                  {appliedFilters.cities.length +
                    appliedFilters.sectors.length +
                    appliedFilters.turnovers.length +
                    appliedFilters.countries.length}
                </Badge>
              )}
            </Button>
            <Dropdown onSelect={handleSortSelect}>
              <Dropdown.Toggle
                variant="outline-primary"
                className="d-flex align-items-center gap-2 rounded-2 shadow-none py-2 px-3"
                aria-label="Sort"
              >
                <BsSortDown size={16} aria-hidden />
                <span>Sort</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="Recently added">Recently added</Dropdown.Item>
                <Dropdown.Item eventKey="First Name">First Name</Dropdown.Item>
                <Dropdown.Item eventKey="Last Name">Last Name</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}

        {(activeTab === "requests" || activeTab === "connections") && (
          <div className="d-flex d-md-none justify-content-end mb-2">
            <Dropdown onSelect={handleSortSelect}>
              <Dropdown.Toggle
                variant="outline-primary"
                className="d-flex align-items-center gap-2 rounded-2 shadow-none py-2 px-3"
                aria-label="Sort"
              >
                <BsSortDown size={16} aria-hidden />
                <span>Sort</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="Recently added">Recently added</Dropdown.Item>
                <Dropdown.Item eventKey="First Name">First Name</Dropdown.Item>
                <Dropdown.Item eventKey="Last Name">Last Name</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}

      {/* Subtitle row: same structure on all tabs so cards start at same position (Bootstrap invisible on Discover) */}
      <div className="d-none d-sm-flex justify-content-between align-items-center mb-1 gap-2 min-w-0">
        {activeTab === "users" && (
          <>
            <h6 className="mb-0 fw-normal text-nowrap connections-mobile-subtitle invisible">Incoming requests</h6>
            <span className="text-muted small mb-0 mb-md-2 mt-0 text-nowrap text-end connections-mobile-subtitle invisible">Accept to add to your network</span>
          </>
        )}
        {activeTab === "requests" && (
          <>
            <h6 className="mb-0 fw-normal text-nowrap connections-mobile-subtitle">Incoming requests</h6>
            <span className="text-muted small mb-0 mb-md-2 mt-0 text-nowrap text-end connections-mobile-subtitle">Accept to add to your network</span>
          </>
        )}
        {activeTab === "connections" && (
          <>
            <h6 className="mb-0 fw-normal connections-mobile-subtitle">Your connections</h6>
            <span className="text-muted small mb-0 mb-md-2 mt-0 connections-mobile-subtitle connections-subtitle">
              {filteredConnectionsListData.length} connected
            </span>
          </>
        )}
      </div>

      <div className="mt-1 mt-md-2 pb-5 pb-md-0">
        {activeTab === "users" && (
          <Suspense fallback={<div className="min-vh-50" aria-hidden />}>
            <UsersList
              users={users}
              loadMoreUsers={loadMoreUsers}
              hasMoreUsers={hasMoreUsers}
            />
          </Suspense>
        )}

        {activeTab === "connections" && (
          <Suspense fallback={<div className="min-vh-50" aria-hidden />}>
            <ConnectionsList
              connections={filteredConnectionsListData}
              refetchConnections={refetchConnections}
            />
          </Suspense>
        )}

        {activeTab === "requests" && (
          <Suspense fallback={<div className="min-vh-50" aria-hidden />}>
            <RequestsList
              requests={filteredRequestsListData}
              sentRequests={filteredSentRequestsListData}
              refetchConnections={refetchConnections}
              refetchConnectionRequests={refetchConnectionRequests}
              refetchSentConnectionRequests={refetchSentConnectionRequests}
            />
          </Suspense>
        )}
      </div>
      </div>
    </div>
  );
};

export default Connections;
